export default `<!DOCTYPE html>
<html lang="en"><head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{product_name} — Digital Product Passport</title>
  <style>
    /* Scantrust Enterprise Design System — applied tokens */
    :root {
      --v-primary: #15BBF0;          /* brand cyan */
      --v-secondary: #1F5FAC;        /* in-product blue */
      --v-accent: #5AD2F8;
      --v-success: #3FC135;
      --v-warning: #FF7F00;
      --v-error: #F23C2F;

      --cyan-wash: #E0F4FD;
      --cyan-3: #B0E2F9;
      --deep-blue: #0789B2;

      --fg-1: #222222;
      --fg-2: #333333;
      --fg-3: #555555;
      --fg-muted: #767676;
      --text-primary: rgba(0,0,0,0.87);
      --text-secondary: rgba(0,0,0,0.60);

      --surface-app: #FAFAFA;
      --surface-card: #FFFFFF;
      --surface-panel: #F1F1F1;
      --border-1: #DADADA;
      --divider: #EDEDED;
      --dot-grey: #D9D9D9;

      --radius-md: 4px;
      --radius-pill: 9999px;

      --elev-2: 0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12);
      --elev-4: 0 2px 4px -1px rgba(0,0,0,.2), 0 4px 5px 0 rgba(0,0,0,.14), 0 1px 10px 0 rgba(0,0,0,.12);

      --font-sans: 'Roboto', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif;
      --font-mono: 'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace;

      --s-2: 8px; --s-3: 12px; --s-4: 16px; --s-5: 20px; --s-6: 24px;
    }

    * { box-sizing: border-box; }

    html, body {
      margin: 0; padding: 0;
      background: var(--surface-app);
      color: var(--fg-3);
      font-family: var(--font-sans);
      font-size: 14px; line-height: 20px;
      -webkit-font-smoothing: antialiased;
    }

    .wrap { max-width: 480px; margin: 0 auto; padding-bottom: 40px; }
    .inner { padding: 0 var(--s-4); }

    /* ---- App bar (v-app-bar) ---- */
    .v-app-bar {
      background: var(--surface-card);
      height: 56px;
      padding: 0 var(--s-4);
      display: flex; align-items: center;
      border-bottom: 1px solid var(--divider);
      box-shadow: var(--elev-4);
      position: sticky; top: 0; z-index: 5;
    }
    .v-app-bar .logo { height: 20px; width: auto; display: block; }

    /* ---- Page title block ---- */
    .titleblock { padding: var(--s-5) var(--s-4) var(--s-4); }
    .titleblock .kicker {
      font-family: var(--font-mono);
      font-size: 11px; letter-spacing: .08em; text-transform: uppercase;
      color: var(--text-secondary); margin: 0 0 var(--s-2);
    }
    .titleblock h1 {
      font-family: var(--font-sans); font-weight: 700;
      font-size: 24px; line-height: 30px; letter-spacing: .7px;
      color: #323232; margin: 0;
    }
    .titleblock .by { margin: 4px 0 0; font-size: 14px; color: var(--text-secondary); }

    /* ---- Card (v-card · elevation-2) ---- */
    .v-card {
      background: var(--surface-card);
      border-radius: var(--radius-md);
      box-shadow: var(--elev-2);
      margin-bottom: var(--s-3);
      overflow: hidden;
    }
    .v-card.outlined { box-shadow: none; border: 1px solid var(--divider); }

    /* Hero */
    .hero-frame {
      position: relative; width: 100%; aspect-ratio: 4 / 3;
      background: var(--cyan-wash); overflow: hidden;
      display: flex; align-items: center; justify-content: center;
    }
    .hero-frame .placeholder { color: var(--cyan-3); }
    .hero-frame .placeholder svg { width: 64px; height: 64px; display: block; }
    .hero-img {
      position: absolute; inset: 0; width: 100%; height: 100%;
      object-fit: cover; display: block; border: none;
    }
    .hero-body { padding: var(--s-4); }
    .hero-body .v-card__title {
      font-family: var(--font-sans); font-weight: 500; font-size: 18px;
      line-height: 24px; letter-spacing: .2px; color: var(--fg-1); margin: 0 0 4px;
    }
    .hero-body .v-card__text { font-size: 14px; line-height: 20px; color: var(--fg-3); margin: 0; }

    /* ---- Authentication result (status pattern · uses --stc-result-color) ---- */
    .result {
      background: var(--surface-card);
      border-radius: var(--radius-md);
      box-shadow: var(--elev-2);
      border-left: 4px solid var(--stc-result-color);
      padding: var(--s-4);
      margin: var(--s-3) 0;
      display: flex; align-items: center; gap: var(--s-3);
    }
    .result .dot {
      flex: 0 0 auto; width: 12px; height: 12px; border-radius: 50%;
      background: var(--stc-result-color);
    }
    .result .label {
      font-size: 12px; color: var(--text-secondary); margin: 0 0 1px;
    }
    .result .value {
      font-size: 16px; font-weight: 500; color: var(--text-primary); margin: 0;
    }
    .result .value .stc-result { color: var(--stc-result-color); font-weight: 700; }

    /* ---- Expansion section (header + body) ---- */
    .panel { list-style: none; }
    .panel > summary {
      list-style: none; cursor: pointer;
      padding: var(--s-4);
      display: flex; align-items: center; justify-content: space-between; gap: var(--s-3);
    }
    .panel > summary::-webkit-details-marker { display: none; }
    .panel > summary .titlewrap { display: flex; align-items: center; gap: var(--s-3); }
    .panel > summary .ico {
      flex: 0 0 auto; width: 32px; height: 32px; border-radius: var(--radius-md);
      background: var(--cyan-wash); color: var(--v-primary);
      display: flex; align-items: center; justify-content: center;
    }
    .panel > summary .ico svg { width: 18px; height: 18px; }
    .panel > summary .htxt {
      font-family: var(--font-sans); font-weight: 500; font-size: 16px;
      letter-spacing: .2px; color: var(--fg-1);
    }
    .panel > summary .chev {
      flex: 0 0 auto; width: 20px; height: 20px; color: var(--text-secondary);
      transition: transform .2s ease;
    }
    .panel[open] > summary .chev { transform: rotate(180deg); }
    .panel-body { padding: 0 var(--s-4) var(--s-4); border-top: 1px solid var(--divider); }
    .panel-body .intro { margin: var(--s-3) 0 var(--s-2); font-size: 13px; color: var(--text-secondary); }

    /* ---- Spec rows ---- */
    .spec {
      display: flex; justify-content: space-between; gap: var(--s-4);
      padding: 10px 0; border-bottom: 1px solid var(--divider); font-size: 14px;
    }
    .spec:last-child { border-bottom: none; }
    .spec .k { color: var(--text-secondary); flex: 1 1 auto; }
    .spec .v { font-weight: 500; color: var(--fg-2); text-align: right; flex: 0 0 auto; max-width: 58%; }
    .spec .v.mono { font-family: var(--font-mono); font-weight: 400; font-size: 13px; }

    /* status inline (dot + Title Case) */
    .stat { display: inline-flex; align-items: center; gap: 6px; }
    .stat .d { width: 9px; height: 9px; border-radius: 50%; }

    /* ---- Composition bars ---- */
    .fibre { margin: var(--s-3) 0 4px; }
    .fibre-head { display: flex; justify-content: space-between; font-size: 14px; margin-bottom: 5px; color: var(--fg-2); }
    .fibre-head .pct { font-weight: 700; }
    .bar { height: 8px; border-radius: var(--radius-pill); background: var(--cyan-wash); overflow: hidden; }
    .bar > span { display: block; height: 100%; background: var(--v-primary); border-radius: var(--radius-pill); }

    /* ---- Chips (v-chip) ---- */
    .chips { display: flex; flex-wrap: wrap; gap: var(--s-2); margin-top: var(--s-3); }
    .v-chip {
      display: inline-flex; align-items: center; height: 28px; padding: 0 12px;
      border-radius: var(--radius-pill); background: var(--surface-panel);
      color: var(--fg-2); font-size: 13px; font-weight: 400;
    }
    .v-chip.primary { background: var(--cyan-wash); color: var(--deep-blue); }
    .v-chip.warning { background: #FFF1DF; color: #8a5a12; }

    /* ---- Supply chain timeline ---- */
    .chain { margin: var(--s-3) 0 2px; padding: 0; list-style: none; }
    .chain li { position: relative; padding: 0 0 var(--s-4) 26px; font-size: 14px; }
    .chain li:last-child { padding-bottom: 0; }
    .chain li::before {
      content: ""; position: absolute; left: 4px; top: 4px;
      width: 10px; height: 10px; border-radius: 50%; background: var(--v-primary);
    }
    .chain li::after {
      content: ""; position: absolute; left: 8px; top: 15px; bottom: -2px;
      width: 2px; background: var(--cyan-3);
    }
    .chain li:last-child::after { display: none; }
    .chain .stage { font-weight: 500; color: var(--fg-2); }
    .chain .place { color: var(--text-secondary); font-size: 13px; }

    /* ---- Metric tiles ---- */
    .metrics { display: grid; grid-template-columns: 1fr 1fr; gap: var(--s-2); margin-top: var(--s-3); }
    .metric { background: var(--cyan-wash); border-radius: var(--radius-md); padding: var(--s-4); text-align: center; }
    .metric .num { font-size: 22px; font-weight: 700; color: var(--deep-blue); line-height: 1.1; }
    .metric .unit { font-size: 12px; color: var(--text-secondary); font-weight: 500; }
    .metric .cap { font-size: 12px; color: var(--text-secondary); margin-top: 4px; }

    /* ---- Button (v-btn · ALL CAPS) ---- */
    .v-btn {
      display: flex; align-items: center; justify-content: center; gap: 8px;
      width: 100%; height: 44px; margin: var(--s-2) 0 4px;
      background: var(--v-primary); color: #FFFFFF;
      border: none; border-radius: var(--radius-md);
      font-family: var(--font-sans); font-weight: 500; font-size: 14px;
      letter-spacing: 0.0892857em; text-transform: uppercase;
      text-decoration: none; box-shadow: var(--elev-2); cursor: pointer;
    }
    .v-btn .material-icons { width: 18px; height: 18px; display: inline-flex; }

    /* ---- Scan meta ---- */
    .scanmeta { margin: var(--s-4) 0 4px; font-size: 12px; color: var(--text-secondary); text-align: center; line-height: 1.7; }
    .scanmeta code { font-family: var(--font-mono); font-size: 12px; color: var(--fg-2); background: var(--surface-panel); padding: 1px 6px; border-radius: var(--radius-md); }

    /* ---- Footer ---- */
    .stc-powered-by { text-align: center; padding: var(--s-6) 0 4px; font-size: 12px; color: var(--text-secondary); }
    .stc-powered-by a { color: var(--v-primary); text-decoration: none; font-weight: 500; }
    .stc-powered-by .sep { margin: 0 var(--s-2); opacity: 0.5; }
  </style>
</head>
<body>
  <div class="wrap">

    <!-- App bar with Scantrust logo -->
    <header class="v-app-bar">
      <svg class="logo" viewBox="0 0 688 96" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Scantrust">
        <g transform="translate(-56,-84)">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M378.698 156.843V117.73H359.758V103.459H379.282V84H395.184V103.459H422.068V117.73H395.184V164.432H422.068V178.703H400.675C384.773 178.703 378.698 169.816 378.698 156.843ZM56 154.054H72.8755C72.8755 161.488 78.1588 165.717 87.5182 165.717C96.8646 165.717 101.758 162.512 101.758 157.258C101.758 153.029 98.7339 149.968 91.9707 148.657L78.8728 146.179C66.7873 143.844 58.726 136.852 58.726 124.022C58.739 111.632 69.2408 102.162 87.0898 102.162C104.939 102.162 117.011 112.229 117.011 126.811H101.434C101.148 119.961 95.4237 115.563 86.934 115.563C79.4569 115.563 74.7058 118.625 74.7058 124.164C74.7058 128.679 77.8732 131.157 84.0652 132.324L96.7348 134.802C109.677 137.293 118.309 142.975 118.309 157.258C118.309 171.84 104.783 180 87.5182 180C67.5143 180 56.4284 169.946 56 154.054ZM586.928 154.054H603.804C603.804 161.488 609.087 165.717 618.446 165.717C627.793 165.717 632.687 162.512 632.687 157.258C632.687 153.029 629.662 149.968 622.899 148.657L609.801 146.179C597.716 143.844 589.654 136.852 589.654 124.022C589.654 111.632 600.156 102.162 618.005 102.162C635.854 102.162 647.94 112.229 647.94 126.811H632.362C632.077 119.961 626.352 115.563 617.862 115.563C610.385 115.563 605.634 118.625 605.634 124.164C605.634 128.679 608.801 131.157 614.993 132.324L627.663 134.802C640.605 137.28 649.251 142.962 649.251 157.245C649.238 171.84 635.711 180 618.446 180C598.443 180 587.357 169.946 586.928 154.054ZM236.762 134.595C216.408 134.595 207.879 143.222 207.879 156.532C207.879 169.803 216.252 180 232.959 180C243.071 180 250.678 176.212 255.079 169.803H256.948L258.506 178.703H271.487V128.536C271.487 113.669 260.349 102.162 240.423 102.162C221.38 102.162 210.475 113.15 210.475 128.108H226.053C226.053 121.673 229.986 115.862 239.995 115.862C250.535 115.862 255.909 121.116 255.909 128.108V134.595H236.762ZM255.909 145.622V148.904C255.909 159.243 248.536 166.78 236.619 166.78C227.533 166.78 222.807 162.123 222.807 156C222.807 149.293 226.91 145.622 237.736 145.622H255.909ZM287.064 103.472L300.045 103.459L301.915 113.241H303.797C307.756 106.832 315.142 102.162 326.008 102.162C342.74 102.162 353.268 112.657 353.268 129.561V178.703H336.393V134.257C336.393 124.06 331.46 117.172 320.971 117.172C310.145 117.185 303.94 124.437 303.94 137.189V178.703H287.064V103.472ZM563.562 178.703L576.543 178.69V103.459H559.668V144.986C559.668 157.738 553.463 164.99 542.637 164.99C532.148 164.99 527.215 158.102 527.215 147.905V103.459H510.34V152.601C510.34 169.505 520.867 180 537.6 180C548.465 180 555.852 175.33 559.811 168.921H561.693L563.562 178.703ZM672.072 117.73V156.843C672.072 169.816 678.147 178.703 694.049 178.703H715.442V164.432H688.558V117.73H715.442V103.459H688.558V84H672.656V103.459H653.132V117.73H672.072ZM435.049 164.432H449.666V117.73H435.049V103.459H462.309L463.608 115.135H466.204C469.397 107.377 476.368 102.162 485.805 102.162H496.06V117.73H485.364C472.733 117.73 466.217 126.305 466.217 141.82V164.432H496.06V178.703H435.01L435.049 164.432ZM144.921 141.081C144.921 155.948 151.191 165.859 163.951 165.859C172.532 165.859 178.023 161.241 179.321 154.054H196.196C194.431 169.129 183.955 180 164.25 180C141.169 180 128.694 164.406 128.694 141.081C128.694 117.898 140.676 102.162 163.743 102.162C183.864 102.162 193.6 112.54 196.196 128.108H179.321C177.828 120.999 172.194 116.432 163.743 116.432C150.983 116.432 144.921 126.499 144.921 141.081Z" fill="#222222"></path>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M729.721 103.459H744V117.73H729.721V103.459ZM729.721 164.433H744V178.703H729.721V164.433Z" fill="#15BBF0"></path>
        </g>
      </svg>
    </header>

    <!-- Title block -->
    <div class="titleblock">
      <p class="kicker">Digital Product Passport</p>
      <h1>{product_name}</h1>
      <p class="by">by {brand_name}</p>
    </div>

    <div class="inner">

      <!-- Hero -->
      <section class="v-card">
        <div class="hero-frame">
          <span class="placeholder" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><path d="M21 15l-5-5L5 21"></path></svg></span>
          <img class="hero-img" src="{product_image}" alt="{product_name}" onerror="this.style.display='none'">
        </div>
        <div class="hero-body">
          <div class="v-card__title">{product_name}</div>
          <p class="v-card__text">{product_description}</p>
        </div>
      </section>

      <!-- Product Identity -->
      <details class="v-card panel" open="">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"></rect><path d="M7 9h.01M7 13h.01M11 9h6M11 13h6"></path></svg></span>
            <span class="htxt">Product Identity</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"></path></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">Unique product identifier and traceability data linking this garment to its passport record.</p>
          <div class="spec"><span class="k">Unique product ID (UPI)</span><span class="v mono">{extended_id}</span></div>
          <div class="spec"><span class="k">Serial number</span><span class="v mono">{serial_number}</span></div>
          <div class="spec"><span class="k">SKU</span><span class="v mono">{product_sku}</span></div>
          <div class="spec"><span class="k">Data carrier</span><span class="v">QR on care label</span></div>
        </div>
      </details>

      <!-- Compliance & Substances -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12l2 2 4-4"></path><path d="M12 3l7 3v5c0 5-3.5 8-7 9-3.5-1-7-4-7-9V6l7-3z"></path></svg></span>
            <span class="htxt">Compliance &amp; Substances</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"></path></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">Substances of concern disclosure, REACH compliance and verified third-party certifications.</p>
          <div class="spec"><span class="k">Substances of concern</span><span class="v">None above threshold</span></div>
          <div class="spec"><span class="k">REACH / SVHC</span><span class="v">Compliant</span></div>
          <div class="spec"><span class="k">Conformity</span><span class="v">EU declaration on file</span></div>
          <div class="chips">
            <span class="v-chip">GOTS</span>
            <span class="v-chip">GRS</span>
            <span class="v-chip">bluesign®</span>
          </div>
        </div>
      </details>

      <!-- Material Composition -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7l9-4 9 4-9 4-9-4z"></path><path d="M3 7v10l9 4 9-4V7"></path></svg></span>
            <span class="htxt">Material Composition</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"></path></svg>
        </summary>
        <div class="panel-body">
          <p>Organic cottong (60%), Recycled polyester (35%), Elastane (5%).</p>
        </div>
      </details>

      <!-- Origin & Supply Chain -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"></path></svg></span>
            <span class="htxt">Origin &amp; Supply Chain</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"></path></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">Country of origin across key Tier 1–3 production stages, tied to facility-level traceability.</p>
          <ul class="chain">
            <li><span class="stage">Raw material</span><br><span class="place">Organic cotton — Gujarat, India</span></li>
            <li><span class="stage">Spinning &amp; weaving</span><br><span class="place">Porto region, Portugal</span></li>
            <li><span class="stage">Dyeing &amp; finishing</span><br><span class="place">Porto region, Portugal</span></li>
            <li><span class="stage">Assembly</span><br><span class="place">Braga, Portugal</span></li>
          </ul>
          <div class="spec" style="margin-top:10px"><span class="k">Facility ID (UFI)</span><span class="v mono">PT-BRG-04827</span></div>
        </div>
      </details>

      <!-- Environmental Footprint -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8a7 7 0 0 1-10 10z"></path><path d="M2 21c0-3 1.85-5.36 5.08-6"></path></svg></span>
            <span class="htxt">Environmental Footprint</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"></path></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">Lifecycle impact based on the EU Product Environmental Footprint (PEF) methodology for apparel.</p>
          <div class="metrics">
            <div class="metric"><div class="num">7.2<span class="unit"> kg</span></div><div class="cap">CO₂e per item</div></div>
            <div class="metric"><div class="num">1,850<span class="unit"> L</span></div><div class="cap">Water use</div></div>
          </div>
          <div class="spec" style="margin-top:12px"><span class="k">Most intensive stage</span><span class="v">Dyeing &amp; finishing</span></div>
          <div class="spec"><span class="k">Resource use</span><span class="v">Renewable energy in production</span></div>
        </div>
      </details>

      <!-- Durability & Repairability -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg></span>
            <span class="htxt">Durability &amp; Repairability</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"></path></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">Standardised physical test results establishing the intrinsic durability baseline.</p>
          <div class="spec"><span class="k">Tensile strength</span><span class="v">Pass — EN ISO 13934-1</span></div>
          <div class="spec"><span class="k">Colourfastness</span><span class="v">Grade 4–5</span></div>
          <div class="spec"><span class="k">Pilling resistance</span><span class="v">Grade 4</span></div>
          <div class="spec"><span class="k">Seam slippage</span><span class="v">Pass</span></div>
          <div class="spec"><span class="k">Designed lifespan</span><span class="v">≥ 5 years / 50 washes</span></div>
        </div>
      </details>

      <!-- Recyclability & Recycled Content -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 19H4.8a2 2 0 0 1-1.7-3l1.9-3.2M9.3 4.2l-1.1 1.9M16.7 4.2l2.4 4.1a2 2 0 0 1-1.7 3H14M12 22l3-3-3-3"></path></svg></span>
            <span class="htxt">Recyclability &amp; Recycled Content</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"></path></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">Design-for-recyclability rating based on fibre mono-materiality and component compatibility.</p>
          <div class="spec"><span class="k">Recyclability score</span><span class="v">Conditionally Recyclable</span></div>
          <div class="spec"><span class="k">Recycled content</span><span class="v">35% post-consumer</span></div>
          <div class="spec"><span class="k">Elastane content</span><span class="v">5% (below 15% limit)</span></div>
          <div class="chips">
            <span class="v-chip primary">Closed-loop eligible</span>
            <span class="v-chip warning">Remove trims before recycling</span>
          </div>
        </div>
      </details>

      <!-- Care, Repair & End of Life -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6l3-3h12l3 3-2 2v13H5V8L3 6z"></path><path d="M9 3v3h6V3"></path></svg></span>
            <span class="htxt">Care, Repair &amp; End of Life</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"></path></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">How to keep this garment in use longer, and how to route it responsibly at end of life.</p>
          <div class="spec"><span class="k">Wash</span><span class="v">30 °C, gentle cycle</span></div>
          <div class="spec"><span class="k">Drying</span><span class="v">Air dry, do not tumble</span></div>
          <div class="spec"><span class="k">Repair service</span><span class="v">care@{product_sku}.repair</span></div>
          <div class="spec"><span class="k">End of life</span><span class="v">Textile collection — separate trims</span></div>
        </div>
      </details>

      <!-- Manufacturer & Operator -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18M5 21V8l5-3v3l5-3v16M19 21V11l-4-2"></path></svg></span>
            <span class="htxt">Manufacturer &amp; Operator</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"></path></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">Legal operator details and EU contact responsible for this product passport.</p>
          <div class="spec"><span class="k">Manufacturer</span><span class="v">{brand_name}</span></div>
          <div class="spec"><span class="k">Operator ID (UOI)</span><span class="v mono">EU-OP-{serial_number}</span></div>
          <div class="spec"><span class="k">Registered address</span><span class="v">Braga, Portugal</span></div>
          <div class="spec"><span class="k">EU contact</span><span class="v">compliance@{product_sku}.eu</span></div>
        </div>
      </details>

      <a class="v-btn" href="{product_url}">VIEW FULL PRODUCT DETAILS</a>

      <p class="scanmeta">Verified in {country} · scanned {scan_count} times<br></p>

      <footer class="stc-powered-by">
        Powered by Scantrust™
        <span class="sep">·</span>
        <a href="https://www.scantrust.com/privacy">Privacy Policy</a>
      </footer>

    </div>
  </div>

</body></html>`
